import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import useCrudFilters from "../../../../hooks/useCrudFilters";
import useCrudTable from "../../../../hooks/useCrudTable";
import { useAppDispatch } from "../../../../hooks/useTypedSelector";
import { CarTableColumns } from "../../domain/columns/carTableColumns";
import { carSchema } from "../../domain/schemas/carSchema";
import {
  Layout,
  LayoutContent,
  LayoutHeader,
  LayoutHeaderActions,
  LayoutHeaderTitle,
} from "../../../general/modules/commons/layout/layout";
import { logout } from "../../../auth/infrastructure/slices/authSlice/authSlice";
import { Breadcrumb } from "../../../general/modules/commons/breadcrumb/breadcrumb";
import { BreadcrumbItem } from "react-bootstrap";
import { Content } from "../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../general/modules/commons/content/contentHeader";
import { CarTableFilters } from "../features/CarTableFilters";
import { useTranslatedColumns } from "../../../general/modules/commons/tableTranslation/translatedColumns";
import { useCrudExport } from "../../../../hooks/useCrudExport";
import { useAuthorization } from "../../../../hooks/useAuthorization";

export const CarPage = () => {
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const { hasRole } = useAuthorization();

  const title = t("Cars");

  const crudFilters = useCrudFilters(
    {
      searchBy: "SearchByRootCar",
      fields: {},
    },
    "carFilter",
  );

  const translatedColumns = useTranslatedColumns(CarTableColumns);

  // Extract license plate filter order for custom sorting
  const licencePlateOrder = useMemo(() => {
    const licenceFilter = crudFilters.filters?.fields?.licence;
    if (
      licenceFilter &&
      "in" in licenceFilter &&
      Array.isArray(licenceFilter.in)
    ) {
      return licenceFilter.in as string[];
    }
    return null;
  }, [crudFilters.filters]);

  // Custom sorting function for license plates
  const customSortData = useMemo(() => {
    if (!licencePlateOrder) {
      return null;
    }

    return (data: unknown[]) => {
      return [...data].sort((a, b) => {
        const carA = a as { licence?: string };
        const carB = b as { licence?: string };
        const licenceA = carA?.licence;
        const licenceB = carB?.licence;

        if (!licenceA || !licenceB) return 0;

        const indexA = licencePlateOrder.indexOf(licenceA);
        const indexB = licencePlateOrder.indexOf(licenceB);

        // If both license plates are in the filter order, sort by that order
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        // If only one is in the order, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        // If neither is in the order, maintain current order
        return 0;
      });
    };
  }, [licencePlateOrder]);

  const addAction = () => {
    if (
      hasRole("dbh-employee") ||
      hasRole("dbh-driver") ||
      hasRole("dbh-order-picker")
    ) {
      return undefined;
    }
    return "/car/add";
  };

  const crud = useCrudTable({
    prefix: "cars",
    entity: "Car",
    columns: translatedColumns,
    schema: carSchema,
    filters: crudFilters.filters,
    addAction: addAction(),
    enableCustomSorting: licencePlateOrder != null,
    customSortData: customSortData,
    defaultSorting: licencePlateOrder ? [] : undefined,
  });

  const exportButton = useCrudExport({
    pageCount: crud.tableInstance.getPageCount(),
    queryFunction: "exportCars",
    queryData: ["licence", { relation: ["name"] }, "is_lease"],
    filters: crudFilters.filters,
  });

  return (
    <Layout
      header={
        <LayoutHeader logout={() => void dispatch(logout())}>
          <LayoutHeaderTitle title={title} />
          <Breadcrumb>
            <BreadcrumbItem>{title}</BreadcrumbItem>
          </Breadcrumb>
          <LayoutHeaderActions>
            {crud.addButton}
            {exportButton.exportButton}
          </LayoutHeaderActions>
        </LayoutHeader>
      }
    >
      <LayoutContent>
        <Content>
          <ContentHeader title={title} className="mb-3" />
          <CarTableFilters {...crudFilters} />
          {crud.table}
        </Content>
      </LayoutContent>
    </Layout>
  );
};
